/**
 * useThreeJSScene Hook
 * 
 * Initialize dan manage Three.js scene dengan:
 * - Scene, Camera, Renderer
 * - Lights (ambient, directional, hemisphere)
 * - Grid helper dan ground plane
 * - Animation loop
 * - Resize handling
 * 
 * @param {React.RefObject} containerRef - Container DOM ref
 * @param {Object} options - Configuration options
 * @returns {Object} Scene refs dan states
 */

import { useEffect, useRef, useState } from 'react'

export function useThreeJSScene(containerRef, options = {}) {
  const {
    backgroundColor = '#1a1a1f',
    cameraFov = 50,
    cameraNear = 0.01,
    cameraFar = 1000,
    lightIntensity = 1,
    ambientLightIntensity = 0.5,
    showFloorGrid = true,
    showGroundPlane = true,
    showDirectionalLights = true,
    enableShadows = true,
    shadowIntensity = 0.3,
    autoRotate = false,
    lockAspectRatio = false,
    exportWidth = 1920,
    exportHeight = 1080,
    useCustomResolution = false,
    transparentBackground = false,
  } = options

  // Refs untuk Three.js objects
  const sceneRef = useRef(null)
  const cameraRef = useRef(null)
  const rendererRef = useRef(null)
  const controlsRef = useRef(null)
  const clockRef = useRef(null)
  const animationIdRef = useRef(null)
  const gridHelperRef = useRef(null)
  const groundPlaneRef = useRef(null)
  const directionalLightsRef = useRef([])
  
  // Refs for animation loop to access latest values
  const lockAspectRatioRef = useRef(lockAspectRatio)
  const useCustomResolutionRef = useRef(useCustomResolution)
  const exportWidthRef = useRef(exportWidth)
  const exportHeightRef = useRef(exportHeight)

  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState(null)
  
  // Keep refs in sync with props
  useEffect(() => {
    lockAspectRatioRef.current = lockAspectRatio
    useCustomResolutionRef.current = useCustomResolution
    exportWidthRef.current = exportWidth
    exportHeightRef.current = exportHeight
  }, [lockAspectRatio, useCustomResolution, exportWidth, exportHeight])

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current || !window.THREE_MODULE) {
      return
    }

    // Prevent double initialization (React 18 strict mode can cause double mount)
    if (sceneRef.current) {
      console.log('⚠️ Scene already exists, skipping init')
      return
    }

    const THREE = window.THREE_MODULE
    console.log('🚀 Initializing scene...')

    try {
      // Get container dimensions
      const width = containerRef.current.clientWidth
      const height = containerRef.current.clientHeight

      if (width === 0 || height === 0) {
        console.warn('Container has no dimensions:', { width, height })
        return
      }

      // Scene
      const scene = new THREE.Scene()
      scene.background = new THREE.Color(backgroundColor)
      sceneRef.current = scene

      // Camera
      const camera = new THREE.PerspectiveCamera(
        cameraFov,
        width / height,
        cameraNear,
        cameraFar
      )
      camera.position.set(0, 2, 5)
      camera.lookAt(0, 0, 0)
      cameraRef.current = camera

      // Renderer
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
      })
      renderer.setSize(width, height)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      renderer.setClearColor(backgroundColor)
      renderer.setClearAlpha(1)
      if (renderer.outputEncoding !== undefined) {
        renderer.outputEncoding = THREE.sRGBEncoding
      }
      renderer.toneMapping = THREE.ACESFilmicToneMapping
      renderer.toneMappingExposure = 1.0

      // Enable soft shadows
      renderer.shadowMap.enabled = enableShadows
      renderer.shadowMap.type = THREE.PCFSoftShadowMap

      containerRef.current.appendChild(renderer.domElement)
      rendererRef.current = renderer

      // Lights
      const ambientLight = new THREE.AmbientLight(0xffffff, ambientLightIntensity)
      scene.add(ambientLight)

      const directionalLight1 = new THREE.DirectionalLight(0xffffff, lightIntensity)
      directionalLight1.position.set(5, 10, 5)
      directionalLight1.castShadow = true
      directionalLight1.visible = showDirectionalLights !== false
      directionalLight1.shadow.mapSize.width = 2048
      directionalLight1.shadow.mapSize.height = 2048
      directionalLight1.shadow.camera.near = 0.5
      directionalLight1.shadow.camera.far = 50
      directionalLight1.shadow.camera.left = -10
      directionalLight1.shadow.camera.right = 10
      directionalLight1.shadow.camera.top = 10
      directionalLight1.shadow.camera.bottom = -10
      directionalLight1.shadow.bias = -0.0001
      scene.add(directionalLight1)

      const directionalLight2 = new THREE.DirectionalLight(
        0xffffff,
        lightIntensity * 0.5
      )
      directionalLight2.position.set(-5, 5, -5)
      directionalLight2.visible = showDirectionalLights !== false
      scene.add(directionalLight2)

      directionalLightsRef.current = [directionalLight1, directionalLight2]

      const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.6)
      scene.add(hemisphereLight)

      // Grid helper (floor line)
      const gridHelper = new THREE.GridHelper(10, 10, 0x888888, 0x444444)
      gridHelper.visible = showFloorGrid
      scene.add(gridHelper)
      gridHelperRef.current = gridHelper

      // Ground plane for shadows
      const groundGeometry = new THREE.PlaneGeometry(100, 100)
      const groundMaterial = new THREE.ShadowMaterial({
        opacity: shadowIntensity,
      })
      const groundPlane = new THREE.Mesh(groundGeometry, groundMaterial)
      groundPlane.rotation.x = -Math.PI / 2
      groundPlane.position.y = -0.01
      groundPlane.receiveShadow = true
      groundPlane.visible = showGroundPlane !== false
      scene.add(groundPlane)
      groundPlaneRef.current = groundPlane

      // OrbitControls - CRITICAL! Untuk camera movement
      const OrbitControls = window.OrbitControls
      if (OrbitControls) {
        const controls = new OrbitControls(camera, renderer.domElement)
        controls.enableDamping = true
        controls.dampingFactor = 0.05
        controls.screenSpacePanning = true
        controls.minDistance = 0.1
        controls.maxDistance = 500
        controls.autoRotate = autoRotate
        controls.autoRotateSpeed = 0.5
        controlsRef.current = controls

        // Expose globally untuk auto-framing
        window.MAIN_CAMERA = camera
        window.ORBIT_CONTROLS = controls

        console.log('✅ OrbitControls initialized')
      } else {
        console.error('❌ OrbitControls not available!')
      }

      // Clock
      clockRef.current = new THREE.Clock()

      // Animation loop - CRITICAL untuk rendering!
      const animate = () => {
        animationIdRef.current = requestAnimationFrame(animate)

        // Update controls
        if (controlsRef.current) {
          controlsRef.current.update()
        }

        // Determine active camera (default or GLB camera)
        let activeCamera = camera
        let isGLBCamera = false
        
        // CRITICAL FIX: Force default orbit camera jika camera keyframes exist!
        // Camera animation system dirancang untuk orbit camera, bukan GLB camera
        const hasCameraKeyframes = window.CAMERA_KEYFRAMES_COUNT > 0
        
        if (hasCameraKeyframes) {
          // Always use default orbit camera untuk camera animation
          activeCamera = camera
          isGLBCamera = false
          // No need to update matrix, orbit camera handled by controls
        } else if (window.SELECTED_CAMERA_REF && window.SELECTED_CAMERA_REF.current !== 'default') {
          // Use GLB camera ONLY if NO camera keyframes
          if (window.MODEL_REF?.current?.userData?.cameras) {
            const cameraData = window.MODEL_REF.current.userData.cameras.find(
              (c) => c.name === window.SELECTED_CAMERA_REF.current
            )
            if (cameraData && cameraData.camera) {
              activeCamera = cameraData.camera
              isGLBCamera = true
              // Update camera matrix for accurate rendering
              activeCamera.updateMatrixWorld(true)
            }
          }
        }

        // CRITICAL FIX: Update camera aspect ratio ALWAYS for correct rendering
        // GLB cameras need aspect ratio updated to match viewport (prevent "gepeng")
        if (lockAspectRatioRef.current || useCustomResolutionRef.current) {
          // When lock/custom is active, use export resolution aspect ratio
          activeCamera.aspect = exportWidthRef.current / exportHeightRef.current
          activeCamera.updateProjectionMatrix()
        } else if (isGLBCamera) {
          // When using GLB camera WITHOUT lock/custom, update to match viewport
          // This prevents GLB camera from being "gepeng" (flattened)
          const containerWidth = containerRef.current?.clientWidth || width
          const containerHeight = containerRef.current?.clientHeight || height
          activeCamera.aspect = containerWidth / containerHeight
          activeCamera.updateProjectionMatrix()
        }

        // Render scene with active camera
        renderer.render(scene, activeCamera)
      }
      
      animate()
      console.log('✅ Animation loop started')

      console.log('Scene initialized successfully')
      setIsReady(true)

      // Handle resize - Simple version (detailed logic in useEffect)
      const handleResize = () => {
        if (!containerRef.current) return
        const width = containerRef.current.clientWidth
        const height = containerRef.current.clientHeight
        camera.aspect = width / height
        camera.updateProjectionMatrix()
        renderer.setSize(width, height)
      }
      window.addEventListener('resize', handleResize)

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize)
        if (animationIdRef.current) {
          cancelAnimationFrame(animationIdRef.current)
        }
        if (renderer.domElement && containerRef.current?.contains(renderer.domElement)) {
          containerRef.current.removeChild(renderer.domElement)
        }
        renderer.dispose()
        scene.clear()
      }
    } catch (err) {
      console.error('❌ Failed to initialize scene:', err)
      console.error('Error details:', err.message)
      console.error('Stack:', err.stack)
      setError(`Failed to initialize 3D scene: ${err.message}`)
    }
  }, [
    backgroundColor,
    cameraFov,
    cameraNear,
    cameraFar,
    lightIntensity,
    ambientLightIntensity,
    showFloorGrid,
    showGroundPlane,
    showDirectionalLights,
    enableShadows,
    autoRotate,
  ])

  // Update grid helper visibility
  useEffect(() => {
    if (gridHelperRef.current) {
      gridHelperRef.current.visible = showFloorGrid
    }
  }, [showFloorGrid])

  // Update directional lights visibility
  useEffect(() => {
    if (directionalLightsRef.current.length === 0) return
    directionalLightsRef.current.forEach((light) => {
      light.visible = showDirectionalLights
    })
  }, [showDirectionalLights])

  // Update shadow settings when props change
  useEffect(() => {
    if (!rendererRef.current || !groundPlaneRef.current) return

    rendererRef.current.shadowMap.enabled = enableShadows

    if (groundPlaneRef.current) {
      groundPlaneRef.current.visible = showGroundPlane && enableShadows
      if (groundPlaneRef.current.material) {
        groundPlaneRef.current.material.opacity = shadowIntensity
      }
    }
  }, [enableShadows, shadowIntensity, showGroundPlane])

  // Handle transparent background toggle - EXACT COPY from BACKUP line 2294-2312
  useEffect(() => {
    if (!rendererRef.current || !sceneRef.current || !window.THREE_MODULE) return

    const THREE = window.THREE_MODULE

    if (transparentBackground) {
      // Set renderer to transparent
      rendererRef.current.setClearAlpha(0)
      // Remove scene background to show checker pattern behind
      sceneRef.current.background = null
    } else {
      // Restore opaque background
      rendererRef.current.setClearAlpha(1)
      // Restore scene background color
      sceneRef.current.background = new THREE.Color(backgroundColor)
    }
  }, [transparentBackground, backgroundColor])

  // Update viewport aspect ratio based on export resolution - EXACT COPY from BACKUP line 820-925
  useEffect(() => {
    if (!rendererRef.current || !cameraRef.current || !containerRef.current) return

    const updateViewport = () => {
      const container = containerRef.current
      const renderer = rendererRef.current
      const camera = cameraRef.current

      if (!container || !renderer || !camera) return

      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight

      // Determine active camera (default or GLB)
      let activeCamera = camera
      if (
        window.SELECTED_CAMERA_REF?.current !== 'default' &&
        window.MODEL_REF?.current?.userData.cameras
      ) {
        const cameraData = window.MODEL_REF.current.userData.cameras.find(
          (c) => c.name === window.SELECTED_CAMERA_REF.current
        )
        if (cameraData && cameraData.camera) {
          activeCamera = cameraData.camera
        }
      }

      // Apply letterbox when lock aspect ratio OR use custom resolution
      if (lockAspectRatio || useCustomResolution) {
        // Calculate target aspect ratio from export resolution
        const targetAspect = exportWidth / exportHeight
        const containerAspect = containerWidth / containerHeight

        let renderWidth, renderHeight, offsetX = 0, offsetY = 0

        if (containerAspect > targetAspect) {
          // Container is wider - fit to height
          renderHeight = containerHeight
          renderWidth = containerHeight * targetAspect
          offsetX = (containerWidth - renderWidth) / 2
        } else {
          // Container is taller - fit to width
          renderWidth = containerWidth
          renderHeight = containerWidth / targetAspect
          offsetY = (containerHeight - renderHeight) / 2
        }

        // Update renderer size
        renderer.setSize(renderWidth, renderHeight)

        // Update camera aspect (for both default and GLB cameras)
        activeCamera.aspect = targetAspect
        activeCamera.updateProjectionMatrix()

        // Position canvas with letterbox
        renderer.domElement.style.position = 'absolute'
        renderer.domElement.style.left = `${offsetX}px`
        renderer.domElement.style.top = `${offsetY}px`
      } else {
        // Normal mode - fill container
        renderer.setSize(containerWidth, containerHeight)
        activeCamera.aspect = containerWidth / containerHeight
        activeCamera.updateProjectionMatrix()

        renderer.domElement.style.position = 'relative'
        renderer.domElement.style.left = '0'
        renderer.domElement.style.top = '0'
      }
    }

    updateViewport()

    // Listen to window resize
    const handleResize = () => {
      setTimeout(updateViewport, 100)
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [lockAspectRatio, exportWidth, exportHeight, useCustomResolution])

  return {
    // Refs untuk access di hooks lain
    sceneRef,
    cameraRef,
    rendererRef,
    clockRef,
    animationIdRef,
    gridHelperRef,
    groundPlaneRef,
    directionalLightsRef,

    // States
    isReady,
    error,

    // Direct access (convenience)
    scene: sceneRef.current,
    camera: cameraRef.current,
    renderer: rendererRef.current,
    clock: clockRef.current,
  }
}

